import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  BulkImportField,
  IntakeForm,
  IntakeFormAuditEntry,
  IntakeFormChange,
  IntakeFormDraftState,
  IntakeFormVersion,
  LayoutRule
} from '@ee/common/models';
import {IntakeFormType} from '@ee/common/enums';

@Injectable({ providedIn: 'root' })
export class IntakeFormService {
  constructor(private http: HttpClient,
              @Inject('environment') private environment) {
  }

  findById(id: string): Observable<IntakeForm> {
    return this.http.get<IntakeForm>(`${this.environment.api_prefix}api/intake-form/${id}`);
  }

  findByType(type: IntakeFormType): Observable<IntakeForm> {
    return this.http.get<IntakeForm>(`${this.environment.api_prefix}api/intake-form/type/${type}`);
  }

  getDefaultsFormByType(type: IntakeFormType): Observable<IntakeForm> {
    return this.http.get<IntakeForm>(`${this.environment.api_prefix}api/intake-form/type/${type}/default`);
  }

  findByTypeAndOrganizationId(type: IntakeFormType, organizationId: string, forceDefault = false): Observable<IntakeForm> {
    let queryOptions = '';
    if (forceDefault) {
      queryOptions = '?force-default=true';
    }
    return this.http.get<IntakeForm>(`${this.environment.api_prefix}api/` +
      `intake-form/type/${type}/organization/${organizationId}${queryOptions}`);
  }

  findBulkImportFieldsByTypeAndOrganizationId(type: IntakeFormType, organizationId: string): Observable<BulkImportField[]> {
    return this.http.get<BulkImportField[]>(`${this.environment.api_prefix}api/` +
      `intake-form/type/${type}/organization/${organizationId}/bulk-import-fields`);
  }

  getReservedFields(): Observable<string[]> {
    return this.http.get<string[]>(`${this.environment.api_prefix}api/intake-form/reserved-fields`);
  }

  save(type: IntakeFormType, value: IntakeForm): Observable<IntakeForm> {
    return this.http.post<IntakeForm>(`${this.environment.api_prefix}api/intake-form/type/${type}`, value);
  }

  // ------------------------------------------------------------------ draft, publish, history

  /**
   * Save the unpublished working copy.
   *
   * What the builder autosaves to. It reaches no client: the public form and the create-eviction
   * wizard read the live document, and a draft only becomes one of those through {@link publish}.
   */
  saveDraft(type: IntakeFormType, value: IntakeForm, baseVersion?: number | null): Observable<IntakeFormDraftState> {
    // The published version the canvas was loaded from. The server branches a new draft from it, so a
    // canvas loaded before someone else published is refused as stale at publish, not relabelled.
    const body = baseVersion == null ? value : { ...value, base_version: baseVersion };
    return this.http.post<IntakeFormDraftState>(
      `${this.environment.api_prefix}api/intake-form/type/${type}/draft`, body);
  }

  /** Whose unpublished work is open, and how far ahead of the published form it is. */
  getDraftState(type: IntakeFormType): Observable<IntakeFormDraftState> {
    return this.http.get<IntakeFormDraftState>(
      `${this.environment.api_prefix}api/intake-form/type/${type}/draft`);
  }

  /** Throw the working copy away. The live form is untouched. */
  discardDraft(type: IntakeFormType): Observable<void> {
    return this.http.delete<void>(`${this.environment.api_prefix}api/intake-form/type/${type}/draft`);
  }

  /** Promote the draft onto the live form. 409 when someone published underneath it. */
  publish(type: IntakeFormType): Observable<IntakeFormVersion> {
    return this.http.post<IntakeFormVersion>(
      `${this.environment.api_prefix}api/intake-form/type/${type}/publish`, {});
  }

  getVersions(type: IntakeFormType): Observable<IntakeFormVersion[]> {
    return this.http.get<IntakeFormVersion[]>(
      `${this.environment.api_prefix}api/intake-form/type/${type}/versions`);
  }

  /** What one version changed against the one before it. */
  getVersionChanges(type: IntakeFormType, version: number): Observable<IntakeFormChange[]> {
    return this.http.get<IntakeFormChange[]>(
      `${this.environment.api_prefix}api/intake-form/type/${type}/versions/${version}/changes`);
  }

  /** Pull an old version back into the draft — never straight onto the live form. */
  restoreVersion(type: IntakeFormType, version: number): Observable<IntakeFormDraftState> {
    return this.http.post<IntakeFormDraftState>(
      `${this.environment.api_prefix}api/intake-form/type/${type}/versions/${version}/restore`, {});
  }

  getAuditTrail(type: IntakeFormType): Observable<IntakeFormAuditEntry[]> {
    return this.http.get<IntakeFormAuditEntry[]>(
      `${this.environment.api_prefix}api/intake-form/type/${type}/audit`);
  }

  getRules(type: IntakeFormType): Observable<LayoutRule[]> {
    return this.http.get<LayoutRule[]>(`${this.environment.api_prefix}api/intake-form/type/${type}/rules`);
  }

  /** Replaced whole: a partial update cannot express "this rule no longer exists". */
  saveRules(type: IntakeFormType, rules: LayoutRule[]): Observable<LayoutRule[]> {
    return this.http.put<LayoutRule[]>(`${this.environment.api_prefix}api/intake-form/type/${type}/rules`, rules);
  }
}
